'use client'

import type { ModelPreview } from '@/types/api/models'
import type {
  Customizations,
  ModelConstraintValidationWithStatus,
  SleeveState,
} from '@/types/sleeve-management'
import type { ModelBuilderSidePane } from './model-builder'

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { createLogger } from '@/lib/logger'
import {
  type ModelConstraint,
  ModelConstraintType,
} from '@/types/api/constraints'
import {
  ModelSelectionOptions,
  SleeveCustomizationStatus,
  type SleeveManagementSteps,
} from '@/types/enums/sleeve-management'

const LOGGER = createLogger('reducers/sleeve-management')

/**
 * Sleeve Management Data Normalization
 *
 * Overview:
 * This slice manages the create/edit sleeve flow, tracking model selection and constraint customizations.
 *
 * Data Flow:
 * 1. When a user selects an existing model, we fetch details from `/model/:model_id/`
 * 2. The API response structure differs from what we need for UI display and the createSleeve endpoint
 * 3. We normalize this data to track customization status and maintain constraint details
 *
 * Example Input (from API):
 * ModelStrategy.constraints[] contains constraints like:
 * {
 *   "filter": {
 *     "or": [],
 *     "type": { "equal_to": "corporate" },
 *     "sp_rating": { "greater_than_or_equal_to": "BBB-" },
 *     "issue_size": { "greater_than": 100000000 },
 *     "coupon_type": { "equal_to": "fixed" },
 *     "country_issue": { "equal_to": "US" },
 *     "liquidity_institutional_aggregate": { "greater_than_or_equal_to": 4 }
 *   },
 *   "id": "universe_01JHTJV8MF878NXWG87KQGK766",
 *   "type": "universe"
 * }
 *
 * Normalized Structure (in Redux):
 * {
 *   [constraint_id]: {
 *     status: SleeveCustomizationStatus, // 'unedited', 'edited', 'removed', or 'new'
 *     ...constraintProperties // Properties vary based on constraint_type
 *   }
 * }
 *
 * Constraint Types:
 * - universe
 * - ladder_policy
 * - diversification
 * - allocation
 * - average_value
 * - trade
 * - buy_universe
 *
 * Each constraint type has its own structure as defined in ModelConstraintValidation.
 * The status field tracks user modifications to enable proper UI feedback and API submissions.
 */

const DEFAULT_SLEEVE_STATE = {
  sleeveName: '',
  minAllocation: 0,
  maxAllocation: 0,
  targetAllocation: 0,
  customizations: {},
  selectedLotIds: [] as string[],
  selectedModel: null as string | null,
  originalConstraints: {},
  extra: {},
  isPreviewLoading: false,
} as const

interface SleeveManagementState {
  accountId: string | null
  sleeveDetails: SleeveState
  modelListSearchTerm: string | null
  modelSelection: ModelSelectionOptions
  currentStep: SleeveManagementSteps | null
  editedLadderPolicyFields: string[]
  activePane: ModelBuilderSidePane | null
  modelPreviewData: ModelPreview | null
  isPreviewLoading: boolean
}

const initialState: SleeveManagementState = {
  accountId: null,
  sleeveDetails: DEFAULT_SLEEVE_STATE,
  modelSelection: ModelSelectionOptions.ModelBased,
  modelListSearchTerm: null,
  currentStep: null,
  editedLadderPolicyFields: [],
  activePane: null,
  modelPreviewData: null,
  isPreviewLoading: false,
}

const sleeveManagementSlice = createSlice({
  name: 'sleeveManagement',
  initialState,
  reducers: {
    setCustomizations: (state, action: PayloadAction<Customizations>) => {
      state.sleeveDetails.customizations = action.payload
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.modelListSearchTerm = action.payload
    },
    setCustomization: (
      state,
      action: PayloadAction<{
        constraint_id: string
        data: ModelConstraint
        status?: SleeveCustomizationStatus
        extra?: unknown
      }>,
    ) => {
      const { constraint_id, data } = action.payload
      const existingCustomization =
        state.sleeveDetails.customizations[constraint_id]

      // If the constraint doesn't exist yet, create a new one
      if (!existingCustomization) {
        state.sleeveDetails.customizations[constraint_id] = {
          constraint: { ...data },
          compliant: false,
          status: action.payload.status ?? SleeveCustomizationStatus.New,
        } as ModelConstraintValidationWithStatus
        return
      }

      // Check for type mismatch
      if (existingCustomization.constraint.type !== data.type) {
        LOGGER.error('Constraint type mismatch', {
          error: new Error('Constraint type mismatch'),
          constraintId: constraint_id,
          originalType: existingCustomization.constraint.type,
          payloadType: data.type,
        })
        return
      }

      // Update existing constraint
      Object.assign(existingCustomization.constraint, data)
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      state.sleeveDetails.customizations[constraint_id]!.status =
        action.payload.status ?? SleeveCustomizationStatus.Edited

      if (
        data.type === ModelConstraintType.LadderPolicy &&
        action.payload.extra
      ) {
        state.editedLadderPolicyFields =
          (action.payload.extra as { fields: string[] }).fields ?? []
      }
    },
    deleteCustomization: (state, action: PayloadAction<string>) => {
      delete state.sleeveDetails.customizations[action.payload]
    },
    setOriginalConstraints: (
      state,
      action: PayloadAction<Record<string, ModelConstraint>>,
    ) => {
      state.sleeveDetails.originalConstraints =
        state.sleeveDetails.originalConstraints || {}

      Object.entries(action.payload).forEach(([constraint_id, data]) => {
        // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
        state.sleeveDetails.originalConstraints![constraint_id] = data
      })
    },
    setModelListSearchTerm: (state, action: PayloadAction<string>) => {
      state.modelListSearchTerm = action.payload
    },
    setSelectedModel: (state, action: PayloadAction<string | null>) => {
      state.sleeveDetails.selectedModel = action.payload
    },
    setModelSelection: (
      state,
      action: PayloadAction<ModelSelectionOptions>,
    ) => {
      state.modelSelection = action.payload
    },
    setCurrentStep: (state, action: PayloadAction<SleeveManagementSteps>) => {
      state.currentStep = action.payload
    },
    setSleeveName: (state, action: PayloadAction<string>) => {
      state.sleeveDetails.sleeveName = action.payload
    },
    setAllocationValues: (
      state,
      action: PayloadAction<{
        min: number | undefined
        target: number
        max: number | undefined
      }>,
    ) => {
      state.sleeveDetails.minAllocation = action.payload.min
      state.sleeveDetails.targetAllocation = action.payload.target
      state.sleeveDetails.maxAllocation = action.payload.max
    },
    setSelectedLotIds: (state, action: PayloadAction<string[]>) => {
      state.sleeveDetails.selectedLotIds = action.payload
    },
    setActivePane: (state, action) => {
      state.activePane = action.payload
    },
    setPreviewData: (state, action) => {
      state.modelPreviewData = action.payload
    },
    setPreviewLoading: (state, action) => {
      state.isPreviewLoading = action.payload
    },
    resetSleeveState: (state) => {
      // Return a completely new state object with initial values
      // This ensures a clean state for each new sleeve creation session
      // We preserve accountId to maintain the context
      return {
        ...initialState,
        accountId: state.accountId,
      }
    },

    setEditedLadderPolicyFields: (state, action: PayloadAction<string[]>) => {
      state.editedLadderPolicyFields = action.payload
    },
  },
})

export const { setActivePane } = sleeveManagementSlice.actions

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default sleeveManagementSlice
